import React from 'react';
import { useLang } from '@/i18n/LanguageContext';
import { Award, Target, Heart, Star } from 'lucide-react';

const Team = () => {
  const { t } = useLang();
  
  if (!t.aboutPage) return null;

  return (
    <section id="team" className="py-12 sm:py-16 md:py-24 bg-slate-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Mission & Vision Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {/* Mission */}
          <div className="bg-slate-50 rounded-3xl p-8 md:p-10 shadow-sm border border-slate-100 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center mb-6">
              <Heart className="w-8 h-8 text-brand-primary" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t.aboutPage.missionTitle}</h3>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              {t.aboutPage.missionDesc}
            </p>
          </div>

          {/* Vision */}
          <div className="bg-slate-50 rounded-3xl p-8 md:p-10 shadow-sm border border-slate-100 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6">
              <Star className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t.aboutPage.visionTitle}</h3>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              {t.aboutPage.visionDesc}
            </p>
          </div>
        </div>

        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-[13px] uppercase tracking-[0.2em] text-brand-primary font-bold mb-3">
            {t.team.kicker}
          </h2>
          <h3 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">
            {t.aboutPage.foundersTitle}
          </h3>
        </div>

        {/* Founders Section */}
        <div className="space-y-12 mb-24">
          {t.aboutPage.founders.map((founder, idx) => (
            <div key={idx} className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden">
              <div className="grid md:grid-cols-12 items-center">
                <div className="md:col-span-5 aspect-square md:aspect-auto md:h-full w-full md:min-h-[400px] bg-slate-100 relative">
                  <img src={founder.image} alt={founder.name} className="absolute inset-0 w-full h-full object-cover object-center md:object-contain" />
                </div>
                <div className="md:col-span-7 p-6 sm:p-8 md:p-16">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-secondary text-brand-primary font-bold text-sm mb-6">
                    <Award className="w-4 h-4" />
                    {founder.role}
                  </div>
                  <h4 className="text-3xl font-bold text-slate-900 mb-4">{founder.name}</h4>
                  <p className="text-slate-600 leading-relaxed text-lg whitespace-pre-line">
                    {founder.bio}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Instructors Section */}
        <div className="text-center mb-16">
          <h3 className="text-4xl font-extrabold text-slate-900">
            {t.aboutPage.instructorsTitle}
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {t.aboutPage.instructors.map((instructor, idx) => (
            <div key={idx} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200 group hover:shadow-md transition-all flex flex-col h-full">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden mb-6 bg-slate-100 relative shrink-0">
                <img src={instructor.image} alt={instructor.name} className="absolute inset-0 w-full h-full object-center object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="flex-1 flex flex-col">
                <h5 className="text-2xl font-bold text-slate-900 mb-1">{instructor.name}</h5>
                <p className="text-brand-primary font-medium mb-4">{instructor.role}</p>
                <p className="text-slate-600 text-sm leading-relaxed flex-1">
                  {instructor.bio}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Team;
